import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useEventListeners, useOutsideClick } from 'src/hooks/utils';
import { useTranslation } from 'react-i18next';
import Divider from 'src/components/ui/Divider/Divider';
import ContainerBody from 'src/components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import { toast } from 'react-toastify';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateProduct } from 'src/services/productsNew';
import { useOverlay } from 'src/providers/OverlayProvider';
import { useProductPackaging } from 'src/services/productPackaging';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import ProductImage from '../ProductOverlay/ProductImage/ProductImage';
import BomComponents, { BomComponentsRef } from '../ProductOverlay/BomComponents/BomComponents';
import Head from '../ProductOverlay/Head/Head';
import Dimensions from '../ProductOverlay/Dimensions/Dimensions';
import PrimaryInfo from '../ProductOverlay/PrimaryInfo/PrimaryInfo';
import Tracking from '../ProductOverlay/Tracking/Tracking';
import Customs from '../ProductOverlay/Customs/Customs';
import TabBar from '../TabBar/TabBar';
import ProductPackaging from '../ProductOverlay/ProductPackaging/ProductPackaging';
import Quality from '../ProductOverlay/Quality/Quality';
import { OverlaySectionHeadline } from '../OverlaySectionHeadline/OverlaySectionHeadline';
import {
  createProductFormSchema,
  formValuesToProduct,
  formValuesToProductPackagings,
  ProductFormValues,
} from '../ProductOverlay/ProductFormTypes';
import { updateProductImage } from '../../../services/products';
import Conditions from '../ProductOverlay/Conditions/Conditions';
import { useProductTagAdd } from '../../../services/tags';

const ProductInventoryLazy = React.lazy(() => import('../ProductOverlay/ProductInventory/ProductInventory'));

const CreateProductOverlay: React.FC = () => {
  const mainRef = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const addProductTagMutation = useProductTagAdd();
  const { onClose } = useOverlay();
  const { createProductAsync } = useCreateProduct();
  // const { createQualityCheckAsync } = useQualityCheckMutations();
  const { createPackagingAsync } = useProductPackaging();
  const [activeTab, setActiveTab] = useState(0);
  const [editModeActive, setEditModeActive] = useState(true);
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);
  const [isEmptySetConfirmationOpen, setIsEmptySetConfirmationOpen] = useState(false);
  const productFormSchema = useMemo(() => createProductFormSchema(), []);
  const [, setHideHeaderActions] = useState(false);

  const { control, setValue, handleSubmit, formState, setError, clearErrors } = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema),
    defaultValues: {},
    mode: 'onChange',
  });

  const { isValid, isSubmitting } = formState;

  const bomComponentsRef = useRef<BomComponentsRef | null>(null);
  const isNonShippable = useWatch({ control, name: 'isNonShippable' });
  const isProductSet = useWatch({ control, name: 'isProductSet' });

  const storableProduct = !isProductSet && !isNonShippable;

  const [pendingTags] = useState<any[]>([]);

  const onSubmit: SubmitHandler<ProductFormValues> = async data => {
    let hadSubsequentError = false;

    try {
      const productData = formValuesToProduct(data);
      const createdProduct = await createProductAsync({
        body: productData,
      });

      if (!createdProduct?.id) {
        throw new Error('Failed to create product');
      }

      const productId = Number(createdProduct.id);

      // const qualityChecks = formValuesToQualityChecks(data.qualityChecks);
      // if (qualityChecks.length > 0) {
      //   await Promise.all(
      //     qualityChecks.map(check =>
      //       createQualityCheckAsync({
      //         params: { path: { product_id: productId } },
      //         body: check,
      //       }).catch(err => {
      //         console.error('Quality check failed:', err);
      //         hadSubsequentError = true;
      //       }),
      //     ),
      //   );
      // }

      const packagings = formValuesToProductPackagings(data.productPackagings);
      if (packagings.length > 0) {
        await Promise.all(
          packagings.map(packaging =>
            createPackagingAsync({
              params: { path: { product_id: productId } },
              body: packaging,
            }).catch(err => {
              console.error('Packaging failed:', err);
              hadSubsequentError = true;
            }),
          ),
        );
      }

      if (isProductSet && bomComponentsRef.current) {
        try {
          await bomComponentsRef.current.createNewBomLines(productId);
        } catch (err) {
          console.error('BOM creation failed:', err);
          hadSubsequentError = true;
        }
      }

      const { image } = data;
      if (image) {
        try {
          await updateProductImage(productId, image);
        } catch (err) {
          console.error('Image upload failed:', err);
          hadSubsequentError = true;
        }
      }

      if (pendingTags && pendingTags.length > 0) {
        await Promise.all(
          pendingTags.map(tag =>
            addProductTagMutation
              .mutateAsync({
                params: { path: { product_id: productId, tag_id: tag.id } },
              })
              .catch(err => {
                console.error('Tag addition failed:', err);
                hadSubsequentError = true;
              }),
          ),
        );
      }

      if (!hadSubsequentError) {
        toast.success(t('success.create_product'));
      } else {
        toast.warn(t('errors.partial_success_some_failed'));
      }

      onClose();
    } catch (e: any) {
      console.error(e);
      const errorMessage = e?.detail || e?.response?.data?.detail || e?.message || t('errors.create_product');
      toast.error(errorMessage, { autoClose: 5000 });
    }
  };

  const { errors } = formState;

  const handleEmptySetConfirm = (confirmed: boolean) => {
    setIsEmptySetConfirmationOpen(false);
    if (confirmed) {
      handleSubmit(onSubmit)();
    }
  };

  const handleHeaderAction = (action: 'cancel' | 'save' | 'close' | 'confirm' | 'draft') => {
    if (action === 'cancel') {
      setIsCancelConfirmationOpen(true);
    } else if (action === 'save') {
      if (
        isProductSet &&
        (!bomComponentsRef.current?.selectedProducts || bomComponentsRef.current.selectedProducts.length === 0)
      ) {
        setIsEmptySetConfirmationOpen(true);
      } else {
        handleSubmit(onSubmit)();
      }
    } else {
      if (editModeActive) {
        setIsCloseConfirmationOpen(true);
      } else {
        onClose();
      }
    }
  };

  const handleConfirmClose = (confirmed: boolean) => {
    if (confirmed) {
      if (isCloseConfirmationOpen) {
        onClose();
      } else {
        setEditModeActive(false);
      }
    }

    setIsCancelConfirmationOpen(false);
    setIsCloseConfirmationOpen(false);
  };

  useOutsideClick(mainRef, e => {
    const event = e as MouseEvent;
    if (mainRef.current && !mainRef.current.contains(event.target as Node)) {
      onClose();
    }
  });

  useEventListeners([{ event: 'keydown' }], e => {
    const event = e as KeyboardEvent;
    if (event.key === 'Escape') {
      onClose();
    }
  });

  const methods = useForm<ProductFormValues>();

  const tabs = [
    {
      label: t('general.product_details'),
      onClick: () => setActiveTab(0),
      visible: () => true,
    },
    {
      label: t('general.product_set'),
      onClick: () => setActiveTab(1),
      visible: () => isProductSet,
    },
    {
      label: t('general.product_packaging'),
      onClick: () => setActiveTab(3),
      visible: () => storableProduct,
    },
  ];

  const tabsToShow = useMemo(() => tabs.filter(t => t.visible()), [isProductSet, isNonShippable, storableProduct]);

  // Reset activeTab if the current tab is not visible whenever tabsToShow changes
  useEffect(() => {
    if (!tabsToShow[activeTab]) {
      setActiveTab(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsToShow]);

  useEffect(() => {
    if (!storableProduct) {
      setValue('length', 0);
      setValue('width', 0);
      setValue('height', 0);
      setValue('weight', 0);
      setValue('productPackagings', null);
    }
  }, [storableProduct]);

  useEffect(() => {
    if (isNonShippable) {
      setValue('listPrice', 0);
      setValue('standardPrice', 0);
      setValue('isNonShippable', true);
    }
  }, [isNonShippable]);

  useEffect(() => {
    if (!isProductSet) {
      bomComponentsRef.current = null;
    }
  }, [isProductSet]);

  useEffect(() => {
    setValue('isProductSet', !!isProductSet);
  }, [setValue]);

  useEffect(() => {
    const handler = () => {
      if (activeTab === 1 && bomComponentsRef.current) {
        setHideHeaderActions(bomComponentsRef.current.isSelectingProducts);
      } else {
        setHideHeaderActions(false);
      }
    };

    handler();
    const interval = setInterval(handler, 100);
    return () => clearInterval(interval);
  }, [activeTab, bomComponentsRef.current?.isSelectingProducts]);

  const isSaveDisabled = useMemo(() => {
    if (isSubmitting) return true;
    if (!isValid) return true;
    return false;
  }, [
    isSubmitting,
    isValid,
    isProductSet,
    isNonShippable,
    storableProduct,
    bomComponentsRef.current?.selectedProducts,
  ]);

  return (
    <>
      <Head
        product={null}
        edit={editModeActive}
        setEdit={setEditModeActive}
        onHandleAction={handleHeaderAction}
        disabled={isSaveDisabled}
      />
      <ContainerBody>
        <form onSubmit={handleSubmit(onSubmit)}>
          <h3 className="typo-title-36 text-full mb-40">{t('general.create_product')}</h3>
          <ProductImage
            control={control}
            name="image"
            product_id={undefined}
            productImage=""
            disabled={!editModeActive}
          />
          <TabBar
            className="mt-40"
            tabs={tabsToShow}
            defaultIndex={0}
            key={tabsToShow.map(tab => tab.label).join('-')}
          />
          <div className="mt-40">
            {activeTab === 0 && (
              <>
                <OverlaySectionHeadline as="h4">{t('general.product_details')}</OverlaySectionHeadline>
                <Conditions lockedUnlessDraft={false} disabled={!editModeActive} control={control} isEditMode={false} />
                <PrimaryInfo
                  setError={methods.setError}
                  clearErrors={methods.clearErrors}
                  control={control}
                  errors={errors}
                  editDisabled={!editModeActive}
                  lockedUnlessDraft={false}
                  isSetProduct={isProductSet ?? false}
                />
              </>
            )}
            {isProductSet && (
              <div style={{ display: activeTab === 1 ? 'block' : 'none' }}>
                <BomComponents ref={bomComponentsRef} hasProductSetEnabled={isProductSet} productId={0} editable />
              </div>
            )}
            {activeTab === 0 && (
              <>
                <Divider className="my-40" />
                {!(isNonShippable || isProductSet) && (
                  <>
                    <Dimensions editModeActive={editModeActive} control={control} errors={errors} volume={null} />
                    <Divider className="my-40" />
                    <Customs control={control} errors={errors} editModeActive={editModeActive} />
                    <Divider className="my-40" />
                    <div className="invisible">
                      <Quality
                        setValue={setValue}
                        productId={0}
                        control={control}
                        errors={errors}
                        editModeActive={editModeActive}
                      />
                      {/* <Divider className="my-40" /> */}
                    </div>
                  </>
                )}
                {/* {!isNonShippable && ( */}
                {/*  <> */}
                {/*    <CreateTags errors={errors} setValue={setValue} initialTags={[]} onChange={handleTagsChange} /> */}
                {/*    <Divider className="my-40" /> */}
                {/*  </> */}
                {/* )} */}
                {!(isNonShippable || isProductSet) && (
                  <>
                    <Tracking control={control} editModeActive={editModeActive} />
                    <Divider className="my-40" />
                  </>
                )}
              </>
            )}
            {activeTab === 3 && (
              <ProductPackaging
                setError={setError}
                clearErrors={clearErrors}
                control={control}
                errors={errors}
                setValue={setValue}
                editModeActive={editModeActive}
              />
            )}
            {activeTab === 4 && (
              <Suspense>
                <ProductInventoryLazy productId={0} />
              </Suspense>
            )}
          </div>
        </form>
        <ConfirmationModal
          isOpen={isCloseConfirmationOpen || isCancelConfirmationOpen}
          title={isCloseConfirmationOpen ? t('product_inventory.close_overlay') : t('product_inventory.exit_edit_mode')}
          description={
            isCloseConfirmationOpen
              ? t('product_inventory.close_overlay_text')
              : t('product_inventory.exit_edit_mode_text')
          }
          onClose={handleConfirmClose}
        />
        <ConfirmationModal
          isOpen={isEmptySetConfirmationOpen}
          title={t('product_inventory.empty_set_title')}
          description={t('product_inventory.empty_set_description')}
          onClose={handleEmptySetConfirm}
        />
      </ContainerBody>
    </>
  );
};

export default CreateProductOverlay;
